import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Menu,
  MenuItem,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HayatLogo from "../../assets/images/AlHayatLogo.svg";
import loginLeafMark from "../../assets/images/loginLeafMark.png";
import Cookies from "js-cookie";
import loginService from "../../services/LoginServices";
import { showToast } from "../component/common/toastNotification";
import { useNavigate } from "react-router-dom";
import { loginDetails } from "../../globalState/actions/authAction/authAction";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../globalState/hooks";
import LoginCopyright from "../../components/LoginCopyright";
import SupportContactLinks from "../../components/SupportContactLinks";
import { applyDocumentDirection } from "../../helper/documentDirection";
import { RTL_INPUT_ICON_SX } from "../../helper/rtlIconButton";
import { NJIM } from "../../theme/njimTheme";

const BRAND_INK = NJIM.ink;
const BRAND_MINT = NJIM.mintLight;
const SURFACE_APP = NJIM.muted;
const SURFACE_SUNKEN = NJIM.muted;
const TEXT_PRIMARY = NJIM.textPrimary;
const TEXT_SECONDARY = NJIM.textSecondary;

const loginTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    backgroundColor: SURFACE_SUNKEN,
    fontSize: 13.5,
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "& fieldset": {
      border: `1px solid ${NJIM.border}`,
      borderRadius: "10px",
    },
    "&:hover fieldset": {
      borderColor: NJIM.border,
    },
    "&.Mui-focused": {
      backgroundColor: NJIM.white,
      boxShadow: "0 0 0 3px rgba(22,45,58,0.06)",
      "& fieldset": {
        borderColor: NJIM.textMuted,
        borderWidth: "1px",
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    py: 1.25,
    color: TEXT_PRIMARY,
    "&::placeholder": {
      color: TEXT_SECONDARY,
      opacity: 1,
    },
    "&::-ms-reveal, &::-ms-clear": {
      display: "none",
    },
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
      {
        WebkitBoxShadow: `0 0 0 1000px ${SURFACE_SUNKEN} inset !important`,
        WebkitTextFillColor: `${TEXT_PRIMARY} !important`,
        caretColor: TEXT_PRIMARY,
        transition: "background-color 9999s ease-out 0s",
      },
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
  },
};

interface LoginFieldProps {
  label: string;
  name: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  error?: string;
  startIcon: React.ReactNode;
  endAdornment?: React.ReactNode;
  forceLtrInput?: boolean;
  /** When set with forceLtrInput, aligns LTR email/phone text in RTL forms (default: left). */
  ltrTextAlign?: "left" | "right";
}

const LoginField: React.FC<LoginFieldProps> = ({
  label,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  autoComplete,
  error,
  startIcon,
  endAdornment,
  forceLtrInput = false,
  ltrTextAlign = "left",
}) => (
  <Box sx={{ mb: error ? 1 : 2 }}>
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: 13.5,
        color: TEXT_PRIMARY,
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <TextField
      fullWidth
      name={name}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      error={!!error}
      sx={{
        ...loginTextFieldSx,
        ...(forceLtrInput
          ? {
              "& .MuiOutlinedInput-input": {
                direction: "ltr",
                unicodeBidi: "plaintext",
                textAlign: ltrTextAlign,
              },
            }
          : {}),
      }}
      inputProps={forceLtrInput ? { dir: "ltr" } : undefined}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{ ...RTL_INPUT_ICON_SX, marginInlineStart: 0.75 }}
          >
            {startIcon}
          </InputAdornment>
        ),
        endAdornment,
      }}
    />
    {error && (
      <Typography sx={{ color: NJIM.danger, fontSize: 12, mt: 0.5 }}>
        {error}
      </Typography>
    )}
  </Box>
);

const GhostLeaf = ({
  width,
  height,
  bottom,
  top,
  insetInlineEnd,
  insetInlineStart,
  opacity,
  rotate,
}: {
  width: number;
  height: number;
  bottom?: number;
  top?: number;
  insetInlineEnd?: number;
  insetInlineStart?: number;
  opacity: number;
  rotate: number;
}) => (
  <Box
    component="img"
    src={loginLeafMark}
    alt=""
    aria-hidden
    sx={{
      position: "absolute",
      width,
      height,
      bottom,
      top,
      ...(insetInlineEnd !== undefined ? { insetInlineEnd } : {}),
      ...(insetInlineStart !== undefined ? { insetInlineStart } : {}),
      opacity,
      transform: `rotate(${rotate}deg)`,
      filter: "grayscale(1) invert(1)",
      mixBlendMode: "screen",
      objectFit: "contain",
      pointerEvents: "none",
      userSelect: "none",
    }}
  />
);

const LoginForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isRememberMe, setIsRememberMe] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [formErrors, setFormErrors] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { i18n } = useTranslation();

  const getInitialLanguage = useCallback((): string => {
    const savedLang = localStorage.getItem("i18nextLng");
    return savedLang && (savedLang === "en" || savedLang === "ar")
      ? savedLang
      : i18n.language || "en";
  }, [i18n.language]);

  const [selectedLang, setSelectedLang] =
    useState<string>(getInitialLanguage());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const changeLanguage = (lang: string) => {
    setSelectedLang(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
    applyDocumentDirection(lang);
    setAnchorEl(null);
  };

  const { t } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  useEffect(() => {
    const savedLang = getInitialLanguage();
    if (savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
    applyDocumentDirection(savedLang);

    const rememberUsername = Cookies.get("rememberUsername");
    const rememberPassword = Cookies.get("rememberPassword");

    if (rememberUsername && rememberPassword) {
      setFormData({ email: rememberUsername, password: rememberPassword });
      setIsRememberMe(true);
    }
  }, [getInitialLanguage, i18n]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, []);

  const handleInputChange =
    (field: keyof typeof formErrors) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const validateForm = () => {
    const errors = { email: "", password: "" };
    let isValid = true;

    if (!formData.email) {
      errors.email = t("login.emailRequired");
      isValid = false;
    }
    if (!formData.password) {
      errors.password = t("login.passwordRequired");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const { email, password } = formData;
      const response = await loginService.login({ username: email, password });
      const finalResponse = response.data as Record<string, unknown>;

      if (finalResponse) {
        if (isRememberMe) {
          Cookies.set("rememberUsername", email, { expires: 7 });
          Cookies.set("rememberPassword", password, { expires: 7 });
        } else {
          Cookies.remove("rememberUsername");
          Cookies.remove("rememberPassword");
        }
        const token = String(finalResponse.token ?? finalResponse.Token ?? "");
        const role = String(finalResponse.role ?? finalResponse.Role ?? "");
        const loginPayload = {
          ...finalResponse,
          token,
          role,
          companyName: String(
            finalResponse.companyName ?? finalResponse.CompanyName ?? ""
          ).trim(),
        };

        localStorage.setItem("token", token);
        localStorage.setItem("login_timestamp", Date.now().toString());
        localStorage.removeItem("session_expired_flag");
        localStorage.removeItem("logging_out");

        showToast(
          (finalResponse.message as string | undefined) || t("login.loginSuccessful"),
          "success"
        );

        const mustChangePassword = Boolean(
          finalResponse.mustChangePassword ?? finalResponse.MustChangePassword
        );
        if (mustChangePassword) {
          sessionStorage.setItem("tempPassword", password);
          sessionStorage.setItem("tempRole", role);
          sessionStorage.setItem("pendingLogin", JSON.stringify(loginPayload));
          navigate("/change-password");
          return;
        }

        dispatch(loginDetails(loginPayload));
        if (role.toLowerCase() === "admin") {
          navigate("/CustomerEnrolment");
        } else if (role.toLowerCase() === "supervisor") {
          navigate("/workflowPortal");
        } else if (role.toLowerCase() === "whm") {
          navigate("/itemControlRelease");
        } else if (role.toLowerCase() === "salesrep") {
          navigate("/codSalesRep");
        } else if (role.toLowerCase() === "salesmanager") {
          navigate("/codSalesManager");
        } else {
          navigate("/dashboard");
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
    }
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const languages = [
    { value: "en", label: "GB English" },
    { value: "ar", label: "SA (Arabic) العربية" },
  ];

  const features = [
    {
      icon: <HubOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureTraceability"),
    },
    {
      icon: <SyncOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureSageIntegration"),
    },
    {
      icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureCodApprovals"),
    },
  ];

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100dvh",
        maxHeight: "100dvh",
        overflow: "hidden",
        bgcolor: NJIM.white,
      }}
    >
      <Box
        sx={{
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: { xs: "flex", lg: "grid" },
          flexDirection: { xs: "column" },
          gridTemplateColumns: { lg: "1fr 1fr" },
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
      {/* Hero / brand panel — matches Figma lg:m-4 lg:rounded-[20px] */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          color: NJIM.white,
          bgcolor: BRAND_INK,
          m: 2,
          borderRadius: "20px",
          height: "calc(100% - 32px)",
          maxHeight: "calc(100% - 32px)",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            "@keyframes njimPulse1": {
              "0%, 100%": { opacity: 0.15, transform: "scale(1)" },
              "50%": { opacity: 0.45, transform: "scale(1.18)" },
            },
            "@keyframes njimPulse2": {
              "0%, 100%": { opacity: 0.12, transform: "scale(1)" },
              "50%": { opacity: 0.38, transform: "scale(1.15)" },
            },
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 420,
              height: 420,
              top: -140,
              right: isRTL ? "auto" : -100,
              left: isRTL ? -100 : "auto",
              borderRadius: "50%",
              bgcolor: NJIM.mint,
              filter: "blur(90px)",
              animation: "njimPulse1 5s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 460,
              height: 460,
              bottom: -160,
              left: isRTL ? "auto" : -130,
              right: isRTL ? -130 : "auto",
              borderRadius: "50%",
              bgcolor: NJIM.mint,
              filter: "blur(100px)",
              animation: "njimPulse2 6s ease-in-out infinite 1.5s",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 280,
              height: 280,
              top: "45%",
              left: "28%",
              borderRadius: "50%",
              bgcolor: NJIM.mintLight,
              filter: "blur(90px)",
              opacity: 0.1,
            }}
          />
          <GhostLeaf
            width={860}
            height={860}
            bottom={-220}
            insetInlineEnd={-200}
            opacity={0.28}
            rotate={-10}
          />
          <GhostLeaf
            width={480}
            height={480}
            top={-130}
            insetInlineStart={-140}
            opacity={0.13}
            rotate={16}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            px: "44px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              mx: "auto",
              borderRadius: "22px",
              bgcolor: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.09)",
              backdropFilter: "blur(22px)",
              WebkitBackdropFilter: "blur(22px)",
              p: "38px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.55)",
                mb: 2,
              }}
            >
              {t("login.njim")}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { lg: "2.15rem" },
                fontWeight: 500,
                lineHeight: 1.25,
                color: NJIM.white,
              }}
            >
              {t("login.officeTitle")}
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: 1,
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                {t("login.tagline")}
              </Box>
            </Typography>
            <Box component="ul" sx={{ mt: 4.5, p: 0, m: 0, listStyle: "none" }}>
              {features.map((feature) => (
                <Box
                  component="li"
                  key={feature.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    mb: 2,
                    "&:last-of-type": { mb: 0 },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: "rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "rgba(255,255,255,0.85)",
                      lineHeight: 1.4,
                    }}
                  >
                    {feature.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <LoginCopyright
          sx={{
            position: "relative",
            px: "44px",
            pb: 4,
            fontSize: 12,
            color: "rgba(255,255,255,0.4)",
          }}
        />
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          height: { lg: "100%" },
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: SURFACE_APP,
          p: { xs: 2.5, lg: 5 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 24,
            right: isRTL ? "auto" : 24,
            left: isRTL ? 24 : "auto",
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={handleLanguageClick}
            sx={{
              border: `1px solid ${NJIM.border}`,
              bgcolor: NJIM.white,
              boxShadow: "0 1px 2px rgba(23,34,55,0.04)",
              "&:hover": { bgcolor: NJIM.white },
            }}
            aria-label="Change language"
          >
            <TranslateOutlinedIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleLanguageClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 180,
                borderRadius: "10px",
                border: `1px solid ${NJIM.border}`,
                boxShadow: "0 4px 16px rgba(23,34,55,0.05)",
              },
            }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.value}
                onClick={() => changeLanguage(lang.value)}
                selected={selectedLang === lang.value}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography sx={{ fontSize: 13 }}>{lang.label}</Typography>
                  {selectedLang === lang.value && (
                    <CheckIcon sx={{ fontSize: 18, color: BRAND_INK }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 1, lg: 2 },
            "@media (max-height: 760px)": {
              alignItems: "flex-start",
              py: 1,
            },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: { xs: 2, lg: 2.5 },
                "@media (max-height: 760px)": {
                  mb: 1.5,
                },
              }}
            >
              <img
                src={HayatLogo}
                alt="Al Hayat Scientific Office"
                style={{
                  height: "clamp(48px, 8vh, 80px)",
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: `1px solid ${NJIM.border}`,
                boxShadow: "0 1px 2px rgba(23,34,55,0.04), 0 4px 16px rgba(23,34,55,0.05)",
                bgcolor: NJIM.white,
                p: { xs: 3, sm: 4 },
                "@media (max-height: 760px)": {
                  p: { xs: 2.5, sm: 3 },
                },
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: 24,
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                  }}
                >
                  {t("login.welcomeBack")}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 14,
                    color: TEXT_SECONDARY,
                  }}
                >
                  {t("login.signInToAccess")}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <LoginField
                  label={t("login.emailAddress")}
                  name="email"
                  placeholder={t("login.emailPlaceholder")}
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  autoComplete="username"
                  error={formErrors.email}
                  forceLtrInput
                  ltrTextAlign={isRTL ? "right" : "left"}
                  startIcon={
                    <EmailOutlinedIcon sx={{ color: TEXT_SECONDARY, fontSize: 16 }} />
                  }
                />

                <LoginField
                  label={t("login.password")}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  autoComplete="current-password"
                  error={formErrors.password}
                  startIcon={
                    <LockOutlinedIcon sx={{ color: TEXT_SECONDARY, fontSize: 16 }} />
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
                        ) : (
                          <VisibilityOffIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading}
                  className="njim-primary-btn"
                  sx={{
                    mt: 1,
                    py: 1.25,
                    minHeight: 40,
                    fontSize: 13,
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={22} sx={{ color: NJIM.white }} />
                  ) : (
                    t("login.signIn")
                  )}
                </Button>
              </Box>
            </Card>

            <Box sx={{ mt: { xs: 2.5, lg: 3 } }}>
              <SupportContactLinks variant="login" />
            </Box>

            <LoginCopyright
              sx={{
                fontSize: 12,
                color: TEXT_SECONDARY,
                textAlign: "center",
                mt: { xs: 1.5, lg: 2 },
                pb: {
                  xs: "max(8px, env(safe-area-inset-bottom, 0px))",
                  lg: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
